/**
 * Accordion Block Script
 *
 * Smooth expand/collapse using Motion's animate() with height.
 * The panel is never hidden — when collapsed it clips to show one line
 * of preview text. Expanding reveals the rest of the content.
 *
 * This file is compiled by Vite with ES6+ support.
 * CSS is compiled separately - do not import it here.
 */

import { animate } from 'motion';

/** Animation duration in seconds. */
const DURATION = 0.35;

/** Shared easing curve. */
const EASE = 'easeOut';

/**
 * Collapsed height: one line of body text.
 * type-body-400 is 16px mobile / 20px desktop, leading 1.4 → 22.4px / 28px.
 * The panel has mt-4 (16px) built into layout, but height only measures content.
 * We use the first child's line-height to compute this dynamically.
 */
function getCollapsedHeight(panel) {
	const firstP = panel.querySelector('.prose p');
	if (!firstP) {
		const panelLineHeight = parseFloat(getComputedStyle(panel).lineHeight);
		return Number.isFinite(panelLineHeight) && panelLineHeight > 0 ? panelLineHeight : 22;
	}

	const styles = getComputedStyle(firstP);
	const lineHeight = parseFloat(styles.lineHeight) || 22;
	const paddingTop = parseFloat(styles.paddingTop) || 0;
	const paddingBottom = parseFloat(styles.paddingBottom) || 0;
	const marginTop = parseFloat(styles.marginTop) || 0;
	const marginBottom = parseFloat(styles.marginBottom) || 0;

	return lineHeight + paddingTop + paddingBottom + marginTop + marginBottom;
}

/**
 * Expand an accordion panel with animation.
 */
function expandPanel(trigger, panel) {
	trigger.setAttribute('aria-expanded', 'true');
	panel.removeAttribute('inert');
	// Drop the ellipsis clamp so the paragraph reflows to its natural multi-line
	// height before we measure scrollHeight.
	panel.classList.remove('is-collapsed');

	// Remove fixed height so we can measure full content.
	const collapsedHeight = panel.offsetHeight;
	panel.style.height = 'auto';
	const fullHeight = panel.scrollHeight;
	panel.style.height = collapsedHeight + 'px';

	// Force reflow so the browser registers the starting height.
	panel.offsetHeight;

	animate(panel, { height: fullHeight + 'px' }, { duration: DURATION, ease: EASE }).then(() => {
		// If the user re-collapsed mid-animation, that handler set its own
		// height/overflow — don't clobber them.
		if (trigger.getAttribute('aria-expanded') !== 'true') return;
		panel.style.height = '';
		panel.style.overflow = '';
	});
}

/**
 * Collapse an accordion panel with animation.
 */
function collapsePanel(trigger, panel) {
	trigger.setAttribute('aria-expanded', 'false');

	const collapsedHeight = getCollapsedHeight(panel);

	// Lock current height so animation has a start value.
	panel.style.height = panel.offsetHeight + 'px';
	panel.style.overflow = 'hidden';

	// Force reflow.
	panel.offsetHeight;

	animate(panel, { height: collapsedHeight + 'px' }, { duration: DURATION, ease: EASE }).then(() => {
		// If the user re-expanded mid-animation, the panel is open again;
		// don't lock it shut with `inert` or stamp the ellipsis clamp on it.
		if (trigger.getAttribute('aria-expanded') !== 'false') return;
		panel.setAttribute('inert', '');
		// Apply ellipsis only after the height has finished animating, so the
		// first paragraph doesn't snap to one line before the panel shrinks.
		panel.classList.add('is-collapsed');
	});
}

document.addEventListener('DOMContentLoaded', () => {
	const blocks = document.querySelectorAll('.rmh-accordion');

	/** Track collapsed panels so we can update their height on resize. */
	const collapsedPanels = new Set();

	blocks.forEach((block) => {
		const triggers = block.querySelectorAll('.rmh-accordion__trigger');

		triggers.forEach((trigger) => {
			const panelId = trigger.getAttribute('aria-controls');
			const panel = document.getElementById(panelId);
			const expanded = trigger.getAttribute('aria-expanded') === 'true';

			// Set initial collapsed state — clip to one line preview.
			if (!expanded) {
				panel.style.height = getCollapsedHeight(panel) + 'px';
				panel.style.overflow = 'hidden';
				panel.classList.add('is-collapsed');
				collapsedPanels.add(panel);
			}

			trigger.addEventListener('click', () => {
				const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

				if (isExpanded) {
					collapsePanel(trigger, panel);
					collapsedPanels.add(panel);
				} else {
					expandPanel(trigger, panel);
					collapsedPanels.delete(panel);
				}
			});
		});
	});

	// Recalculate collapsed heights when fluid type changes with viewport.
	window.addEventListener('resize', () => {
		for (const panel of collapsedPanels) {
			panel.style.height = getCollapsedHeight(panel) + 'px';
		}
	});
});
